import { cn } from '@/core/utils';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import styles from './latestInHdfc.module.scss';
import { openUrlInNewTab } from '@/core/utils/general.utils';
import { classNames } from '@/core/utils/class.utils';
import { useDashboard } from '@/contexts/dashboard/dashboard.context';
import { useClevertap } from '@/contexts/analytics/analytics.context';
import { getImageUrlFromArticle } from '@/core/utils/blog.utils';
import moment from 'moment';
import { H6 } from '@/design-system/text/h6/h6';
import { Label } from '@/design-system/text/label/label';
import { Caption } from '@/design-system/text/caption/caption';
import { CaptionM } from '@/design-system/text/caption-m/caption-m';
const numberOfCardsInCarousal = 2;

export default function LatestInHdfc() {
  const { dashboardLabels, popularArticles } = useDashboard();
  const { dashboardLatestInHdfcCms } = dashboardLabels;
  const clevertap = useClevertap();
  const [currentIndex, setCurrentIndex] = useState(0);

  const maxIndex = useMemo(() => {
    return Math.ceil(popularArticles.length / numberOfCardsInCarousal) - 1;
  }, [popularArticles.length]);

  const handleNextClick = () => {
    setCurrentIndex((prevIndex) => Math.min(prevIndex + 1, maxIndex));
  };

  const handlePrevClick = () => {
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  if (!popularArticles.length) {
    return null;
  }

  return (
    <>
      <div className={cn(styles['lat-main-con'])}>
        <div className={styles['lat-chev-txt-con']}>
          <H6 text={dashboardLatestInHdfcCms.title} />
          {popularArticles.length > 2 ? (
            <div className={styles['lat-chevron-con']}>
              <IconChevronLeft
                size={24}
                color={
                  currentIndex === 0
                    ? 'var(--disabled)'
                    : 'var(--primary-color)'
                }
                onClick={handlePrevClick}
              />
              <IconChevronRight
                size={24}
                color={
                  currentIndex === maxIndex
                    ? 'var(--disabled)'
                    : 'var(--primary-color)'
                }
                onClick={handleNextClick}
              />
            </div>
          ) : null}
        </div>

        <div className={styles['lat-card-slider']}>
          <div
            className={classNames(styles['lat-card-track'], {
              [styles['one-lat-card-track']]: popularArticles.length < 2,
            })}
            style={
              {
                '--transformed-value': `translateX(-${currentIndex * 100}%)`,
              } as React.CSSProperties
            }
          >
            {popularArticles.map(({ strapiData }, index) => (
              <div
                key={index}
                className={classNames(styles['lat-card'], {
                  [styles['lat-one-card']]: popularArticles.length < 2,
                })}
              >
                <div className={styles['img-tag-con']}>
                  <Image
                    src={getImageUrlFromArticle(strapiData)}
                    alt="latestCard"
                    width={600}
                    height={600}
                    className={styles['latest-img']}
                  />
                  <div className={styles['tag-con']}>
                    <Caption
                      text={
                        strapiData.category.name.toUpperCase().split(' ')[0]
                      }
                      className={styles['tag-txt']}
                    />
                  </div>
                </div>
                <div className={styles['lat-txt-con']}>
                  <div className={styles['lat-top-txt']}>
                    <Label
                      text={strapiData.title}
                      maxLines={2}
                      className={styles['title']}
                    />

                    {strapiData.description ? (
                      <Caption
                        text={strapiData.description}
                        maxLines={2}
                        className={styles['desc']}
                      />
                    ) : null}
                  </div>
                  <div className={styles['lat-bottom-txt']}>
                    <Caption
                      text={getDaysAgo(strapiData.postedOn)}
                      className={styles['days']}
                    />

                    {strapiData.timeToRead && (
                      <div className={styles['dot-con']} />
                    )}
                    <div className={styles['dot-readtxt-mb']}>
                      <Caption
                        text={strapiData.timeToRead}
                        className={styles['days']}
                      />
                      <div className={styles['dot-mb']} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          onClick={() => {
            //TODO: Uncomment this when the blogs page is live
            // openUrlOrPush('/blogs', router);
            //ADDED REDIRECTION TO BLOGS PAGE
            openUrlInNewTab('https://www.hdfcergo.com/blog?page=1');
            if (dashboardLatestInHdfcCms.buttonTextClevertapEvent?.name) {
              clevertap?.sendButtonEvent(
                dashboardLatestInHdfcCms.buttonTextClevertapEvent.name,
                dashboardLatestInHdfcCms.buttonTextClevertapEvent.payload
              );
            }
          }}
          className={styles['lat-view-more']}
        >
          <CaptionM
            text={dashboardLatestInHdfcCms.buttonText}
            className={styles['lat-view-more-txt']}
          />
        </div>
      </div>
    </>
  );
}

const getDaysAgo = (date?: string): string => {
  if (!date) {
    return '';
  }
  const now = moment();
  const postedDate = moment(date, 'YYYY-MM-DD');
  const yearsAgo = now.diff(postedDate, 'years');
  const monthsAgo = now.diff(postedDate, 'months');
  const daysAgo = now.diff(postedDate, 'days');

  if (yearsAgo > 0) {
    return yearsAgo === 1 ? '1 year ago' : `${yearsAgo} years ago`;
  } else if (monthsAgo > 0) {
    return monthsAgo === 1 ? '1 month ago' : `${monthsAgo} months ago`;
  } else if (daysAgo === 0) {
    return 'Today';
  } else if (daysAgo === 1) {
    return '1 day ago';
  } else {
    return `${daysAgo} days ago`;
  }
};
